import { ShopifyListingStatus } from "@redotech/crdb-enums/product/shopify-listing-status";
import type { SkuMatcher } from "@redotech/util/sku-matcher";

export enum ExchangeEligibilitySurface {
  AGENT_FAMILIES = "agent_families",
  AGENT_RECOMMENDABLE_LISTINGS = "agent_recommendable_listings",
  MERCHANT_SEARCH = "merchant_search",
  PORTAL_EXCHANGE_GROUPS = "portal_exchange_groups",
  PORTAL_VARIANT_PICKER = "portal_variant_picker",
  SAME_ITEM = "same_item",
  SHOP_NOW = "shop_now",
}

export enum ExchangeEligibility {
  ELIGIBLE = "eligible",
  ARCHIVED = "archived",
  DRAFT = "draft",
  UNPUBLISHED = "unpublished",
  EXCLUDED = "excluded",
  OUT_OF_STOCK = "out_of_stock",
}

export enum OnlineStorePublication {
  PUBLISHED = "published",
  UNPUBLISHED = "unpublished",
  UNKNOWN = "unknown",
}

type BlockableStatus = Exclude<
  ShopifyListingStatus,
  ShopifyListingStatus.Active
>;

interface ExchangeEligibilityRules {
  readonly blockedStatuses: readonly BlockableStatus[];
  readonly requireProductOnlineStore: boolean;
  readonly requireVariantOnlineStore: boolean;
  readonly requireStock: boolean;
}

export interface ExchangeEligibilityPolicy extends ExchangeEligibilityRules {
  readonly surface: ExchangeEligibilitySurface;
  readonly excludedTags: ReadonlySet<string>;
  readonly excludedSkus: SkuMatcher | undefined;
}

interface ExchangeEligibilityOptions {
  readonly excludedTags?: Iterable<string>;
  readonly excludedSkus?: SkuMatcher;
  readonly requireStock?: boolean;
  readonly requireVariantOnlineStore?: boolean;
}

/** Unset fields are unknown and never make a variant ineligible. */
export interface ExchangeEligibilityCandidate {
  readonly productStatus?: string | null;
  readonly productOnlineStore?: OnlineStorePublication;
  readonly variantOnlineStore?: OnlineStorePublication;
  readonly tags?: readonly string[] | null;
  readonly sku?: string | null;
  readonly inStock?: boolean;
  readonly excluded?: boolean;
}

const LISTING_STATUSES = [
  ShopifyListingStatus.Active,
  ShopifyListingStatus.Draft,
  ShopifyListingStatus.Archived,
  ShopifyListingStatus.Unlisted,
];

export const BLOCKED_STATUS_REASON: Record<
  BlockableStatus,
  ExchangeEligibility
> = {
  [ShopifyListingStatus.Draft]: ExchangeEligibility.DRAFT,
  [ShopifyListingStatus.Archived]: ExchangeEligibility.ARCHIVED,
  [ShopifyListingStatus.Unlisted]: ExchangeEligibility.UNPUBLISHED,
};

const SURFACE_RULES: Record<
  ExchangeEligibilitySurface,
  ExchangeEligibilityRules
> = {
  [ExchangeEligibilitySurface.AGENT_FAMILIES]: {
    blockedStatuses: [
      ShopifyListingStatus.Draft,
      ShopifyListingStatus.Archived,
      ShopifyListingStatus.Unlisted,
    ],
    requireProductOnlineStore: false,
    requireVariantOnlineStore: true,
    requireStock: true,
  },
  [ExchangeEligibilitySurface.AGENT_RECOMMENDABLE_LISTINGS]: {
    blockedStatuses: [
      ShopifyListingStatus.Draft,
      ShopifyListingStatus.Archived,
      ShopifyListingStatus.Unlisted,
    ],
    requireProductOnlineStore: true,
    requireVariantOnlineStore: false,
    requireStock: false,
  },
  [ExchangeEligibilitySurface.MERCHANT_SEARCH]: {
    blockedStatuses: [ShopifyListingStatus.Unlisted],
    requireProductOnlineStore: false,
    requireVariantOnlineStore: false,
    requireStock: false,
  },
  [ExchangeEligibilitySurface.PORTAL_EXCHANGE_GROUPS]: {
    blockedStatuses: [],
    requireProductOnlineStore: true,
    requireVariantOnlineStore: false,
    requireStock: false,
  },
  [ExchangeEligibilitySurface.PORTAL_VARIANT_PICKER]: {
    blockedStatuses: [ShopifyListingStatus.Archived],
    requireProductOnlineStore: false,
    requireVariantOnlineStore: true,
    requireStock: true,
  },
  [ExchangeEligibilitySurface.SAME_ITEM]: {
    blockedStatuses: [ShopifyListingStatus.Archived],
    requireProductOnlineStore: false,
    requireVariantOnlineStore: false,
    requireStock: true,
  },
  [ExchangeEligibilitySurface.SHOP_NOW]: {
    blockedStatuses: [],
    requireProductOnlineStore: false,
    requireVariantOnlineStore: true,
    requireStock: true,
  },
};

const STRICTER_RULES: ExchangeEligibilityRules = {
  blockedStatuses: [ShopifyListingStatus.Draft, ShopifyListingStatus.Archived],
  requireProductOnlineStore: true,
  requireVariantOnlineStore: true,
  requireStock: true,
};

export function buildExchangeEligibilityPolicy(
  surface: ExchangeEligibilitySurface,
  options: ExchangeEligibilityOptions = {},
): ExchangeEligibilityPolicy {
  const rules = SURFACE_RULES[surface];
  return {
    surface,
    blockedStatuses: rules.blockedStatuses,
    requireProductOnlineStore: rules.requireProductOnlineStore,
    requireVariantOnlineStore:
      options.requireVariantOnlineStore ?? rules.requireVariantOnlineStore,
    requireStock: options.requireStock ?? rules.requireStock,
    excludedTags: new Set([...(options.excludedTags ?? [])].map(normalizeTag)),
    excludedSkus: options.excludedSkus,
  };
}

export function buildStricterExchangeEligibilityPolicy(
  policy: ExchangeEligibilityPolicy,
): ExchangeEligibilityPolicy {
  return { ...policy, ...STRICTER_RULES };
}

export function allowedListingStatuses(
  policy: Pick<ExchangeEligibilityPolicy, "blockedStatuses">,
): ShopifyListingStatus[] {
  const blocked = new Set<ShopifyListingStatus>(policy.blockedStatuses);
  return LISTING_STATUSES.filter((status) => !blocked.has(status));
}

export function evaluateVariant(
  policy: ExchangeEligibilityPolicy,
  variant: ExchangeEligibilityCandidate,
): ExchangeEligibility {
  const status = parseListingStatus(variant.productStatus);
  const blockedStatus = policy.blockedStatuses.find(
    (blocked) => blocked === status,
  );
  if (blockedStatus) {
    return BLOCKED_STATUS_REASON[blockedStatus];
  }
  if (
    policy.requireProductOnlineStore &&
    variant.productOnlineStore === OnlineStorePublication.UNPUBLISHED
  ) {
    return ExchangeEligibility.UNPUBLISHED;
  }
  if (
    policy.requireVariantOnlineStore &&
    variant.variantOnlineStore === OnlineStorePublication.UNPUBLISHED
  ) {
    return ExchangeEligibility.UNPUBLISHED;
  }
  const hasExcludedTag = (variant.tags ?? []).some((tag) =>
    policy.excludedTags.has(normalizeTag(tag)),
  );
  if (
    variant.excluded ||
    hasExcludedTag ||
    policy.excludedSkus?.matches(variant.sku)
  ) {
    return ExchangeEligibility.EXCLUDED;
  }
  if (policy.requireStock && variant.inStock === false) {
    return ExchangeEligibility.OUT_OF_STOCK;
  }
  return ExchangeEligibility.ELIGIBLE;
}

export function isExchangeEligible(
  policy: ExchangeEligibilityPolicy,
  variant: ExchangeEligibilityCandidate,
): boolean {
  return evaluateVariant(policy, variant) === ExchangeEligibility.ELIGIBLE;
}

export function countStricterPolicyExclusions(
  policy: ExchangeEligibilityPolicy,
  candidates: Iterable<ExchangeEligibilityCandidate>,
): Map<ExchangeEligibility, number> {
  const stricter = buildStricterExchangeEligibilityPolicy(policy);
  const counts = new Map<ExchangeEligibility, number>();
  for (const candidate of candidates) {
    if (!isExchangeEligible(policy, candidate)) {
      continue;
    }
    const reason = evaluateVariant(stricter, candidate);
    if (reason === ExchangeEligibility.ELIGIBLE) {
      continue;
    }
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return counts;
}

function parseListingStatus(
  status: string | null | undefined,
): ShopifyListingStatus | undefined {
  const normalized = status?.toUpperCase();
  return LISTING_STATUSES.find((listingStatus) => listingStatus === normalized);
}

function normalizeTag(tag: string): string {
  return tag.toLowerCase().trim();
}
